import React, { useEffect, useState } from "react";
import { X, Megaphone, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "../../supabaseClient";

// Top-of-app banner driven by public.admin_announcements. Exactly one row
// can be is_active = true at a time (enforced by the RPC). Each user can
// dismiss locally — we store the dismissed id in localStorage so it stays
// gone until the admin posts a new one (different id, fresh surface).
export function AnnouncementBanner({ C, dark }) {
  const [a, setA] = useState(null);

  const load = async () => {
    const { data } = await supabase
      .from("admin_announcements")
      .select("id, message, link_url, link_label, tone")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1);
    const row = data?.[0];
    if (!row) { setA(null); return; }
    try {
      const dismissed = JSON.parse(localStorage.getItem("bam_dismissed_announcements") || "[]");
      if (dismissed.includes(row.id)) { setA(null); return; }
    } catch {}
    setA(row);
  };

  useEffect(() => {
    load();
    // Cheap re-poll on focus so new announcements show up without a reload.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!a) return null;

  const dismiss = () => {
    try {
      const current = JSON.parse(localStorage.getItem("bam_dismissed_announcements") || "[]");
      localStorage.setItem("bam_dismissed_announcements", JSON.stringify([...new Set([...current, a.id])]));
    } catch {}
    setA(null);
  };

  // Two palettes, because one does not work.
  //
  // These were a single set tuned for the dark app: a 14% tint of the
  // brand cream, with #E2DD9F for the icon and the link. Over #0A0A0A
  // that is a soft gold bar. Over the light theme's #FFFFFF it is a
  // white bar with pale cream text on it, which is the washed-out strip
  // Coleman kept pointing at. Alpha off a light accent only ever works
  // on a dark ground, and a colour that means "look here" has to be
  // darker than the page, not lighter.
  //
  // The light values are the same three tones taken down far enough to
  // hold their own on white, rather than the dark ones reused.
  const palettes = dark
    ? {
        info:    { bg: "rgba(226,221,159,0.14)", border: "rgba(226,221,159,0.55)", fg: "#E2DD9F" },
        warn:    { bg: "rgba(248,113,113,0.14)", border: "rgba(248,113,113,0.45)", fg: "#F87171" },
        success: { bg: "rgba(90,181,132,0.16)",  border: "rgba(90,181,132,0.45)",  fg: "#5AB584" },
      }
    : {
        info:    { bg: "#FAF6E0", border: "#D8CC84", fg: "#6E5C0E" },
        warn:    { bg: "#FDF0F0", border: "#E8A2A2", fg: "#A32219" },
        success: { bg: "#EFF7F2", border: "#9FCFB5", fg: "#1D6640" },
      };
  const icons = {
    info:    <Megaphone size={14}/>,
    warn:    <AlertTriangle size={14}/>,
    success: <CheckCircle2 size={14}/>,
  };
  const tone = a.tone || "info";
  const palette = { ...(palettes[tone] || palettes.info), icon: icons[tone] || icons.info };

  // A card that floats under the top bar rather than a strip across the
  // top of the app. The strip was the full width of the window and sat
  // above the app shell, which is a full window tall, so whenever an
  // announcement was up the page was 36px taller than the window and
  // scrolled, sidebar and all (Coleman, 2026-10-07). Floating, it takes no
  // room, and it reads as news rather than as a warning.
  const surface = dark ? "#141414" : "#FFFFFF";
  return (
    <div role="status" className="bam-announcement" style={{
      position:"fixed", zIndex:9500,
      top:"calc(env(safe-area-inset-top, 0px) + 66px)", right:"max(16px, env(safe-area-inset-right, 0px))",
      width:"min(400px, calc(100vw - 32px))",
      background:surface, border:`1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius:14, padding:"12px 12px 12px 14px", display:"flex", alignItems:"flex-start", gap:12,
      boxShadow: dark ? "0 18px 50px rgba(0,0,0,0.55)" : "0 12px 32px rgba(0,0,0,0.12)",
      fontFamily:"'DM Sans', sans-serif", color: C?.text || (dark ? "#F5F5F5" : "#111111"),
      animation:"bamAnnIn .35s cubic-bezier(.32,.72,0,1)",
    }}>
      <span style={{width:30,height:30,borderRadius:9,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
        background:palette.bg,color:palette.fg,border:`1px solid ${palette.border}`}}>{palette.icon}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,lineHeight:1.45,fontWeight:600}}>{a.message}</div>
        {a.link_url && (() => {
          // An announcement pointing somewhere inside the app has to
          // navigate, not spawn a second copy of the app in a new tab.
          const internal = a.link_url.startsWith("#") || a.link_url.startsWith("/");
          return (
            <a href={a.link_url} onClick={internal ? dismiss : undefined}
              {...(internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
              style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:8,fontSize:12.5,fontWeight:800,
                color: dark ? palette.fg : "#111", background: dark ? "transparent" : palette.bg,
                textDecoration:"none",padding:"5px 12px",borderRadius:16,border:`1px solid ${palette.border}`}}>
              {a.link_label || "Open"} →
            </a>
          );
        })()}
      </div>
      <button onClick={dismiss} aria-label="Dismiss"
        style={{background:"transparent",border:"none",cursor:"pointer",width:28,height:28,margin:"-4px -4px 0 0",borderRadius:8,
          color:C?.textMid || "#9CA3AF",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <X size={14}/>
      </button>
    </div>
  );
}

export default AnnouncementBanner;
